import { useEffect, useRef } from 'react'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { LOGOUT } from '../../../store/auth/AuthConstants'
import { safeLocalStorage } from '@quickmd-llc/frontend-utils'
import { AUTH_VALIDATE_PAGE_PATH } from '../../../utils/constants'
import { INACTIVITY_TIMEOUT_MS, msUntilInactive } from '../../../utils/inactivity'
import { useInVisitKeepAlive } from '../../../hooks/useInVisitKeepAlive'

const TIMEOUT_LENGTH = INACTIVITY_TIMEOUT_MS

/**
 * Hoisted out of the component so the effect below can register and remove the same set.
 *
 * `load` is deliberately NOT here. A page loading is not the provider doing something, but
 * the handler treats every entry as activity and rewrites `lastUserAction` to now — so a
 * workstation left for 40 minutes and then reloaded (or a tab the browser restored) had
 * its idle window silently restarted, and `App.tsx`'s mount check read a fresh timestamp
 * instead of the stale one it exists to catch. That is the HIPAA §164.312(a)(2)(iii)
 * automatic logoff not running in exactly the case it is for.
 *
 * Dropping it costs nothing: the effect arms the countdown on mount from the stored
 * timestamp, so the timer is running whether or not `load` ever fires. A provider who is
 * genuinely present still resets the window by moving, clicking, scrolling or typing, and
 * `useInVisitKeepAlive` covers the one case with no input at all — listening during a
 * video visit.
 */
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'click', 'scroll', 'keypress', 'keyup', 'keydown']

// @ts-expect-error TS7031 -- MIGRATION: not approved, fix when encountered
export const AppLogout = ({ children }) => {
  const dispatch = useDispatch()
  // Counterpart to the idle timer below: a visit in progress counts as activity even
  // when the provider is only listening. Mounted here so it covers every secured page
  // and survives route changes mid-visit.
  useInVisitKeepAlive()
  const timerRef = useRef()
  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }

  const logoutAction = () => {
    if (
      !(window.location.pathname === '/') &&
      !(window.location.pathname === '/forgot-password') &&
      !(window.location.pathname === '/resetPassword') &&
      !(window.location.pathname === '/verifyEmail') &&
      !(window.location.pathname === AUTH_VALIDATE_PAGE_PATH)
    ) {
      window.onbeforeunload = () => {}
      window.onpopstate = () => {}
      // Clearing and navigating used to happen here, 200ms later, on a timer. That is now
      // `logoutTeardownMiddleware`'s job for every logout path rather than this one: the
      // bare `safeLocalStorage.clear()` never paused the persistoid, so its next throttle
      // tick wrote the roster straight back into the storage this had just emptied. See
      // QMD-4332.
      dispatch({ type: LOGOUT, reason: 'inactivity-timeout' })
    }
  }
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const handleLogoutTimer = (length) => {
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    timerRef.current = setTimeout(() => {
      resetTimer()
      // Remaining idle time. A missing `lastUserAction` restarts the full window instead
      // of resolving to `1800000` (`JSON.parse(null) + TIMEOUT_LENGTH`), which was always
      // in the past and logged the provider out on the very next tick.
      const remainingMs = msUntilInactive(safeLocalStorage.get('lastUserAction'), moment().valueOf())

      if (remainingMs <= 0) {
        logoutAction()
      } else {
        handleLogoutTimer(remainingMs)
      }
    }, length || TIMEOUT_LENGTH)
  }

  /**
   * `SecuredHIPAAPageElem` is the `element` of EVERY secured route, so this component
   * unmounts and remounts on every navigation.
   *
   * The listeners therefore have to be removed on cleanup. They previously were not — and
   * could not be: they were registered as anonymous arrows with `capture: true`, while the
   * only `removeEventListener` call passed `resetTimer`, a function that was never
   * registered as a handler. So each navigation added eight more capture-phase window
   * listeners that lived until the tab closed, and every subsequent `mousemove` ran all of
   * them: N localStorage read-delete-write cycles and N recursive timer chains per pointer
   * event, growing all shift. That is exactly the wrong tax to levy during a video visit.
   *
   * Arming the timer on mount is the other half. Without it the idle countdown only ever
   * started on a user event, so clearing the previous instance's timer on unmount would
   * have left a navigated-then-abandoned workstation with no armed countdown at all — the
   * HIPAA §164.312(a)(2)(iii) automatic logoff quietly not running. `lastUserAction` is
   * origin-shared and read fresh on every tick, so re-arming across a navigation resumes
   * the existing window rather than restarting it.
   */
  useEffect(() => {
    const handleActivity = () => {
      if (safeLocalStorage.get('lastUserAction')) {
        safeLocalStorage.remove('lastUserAction')
      }
      safeLocalStorage.set('lastUserAction', JSON.stringify(moment().valueOf()))
      resetTimer()
      // @ts-expect-error TS2554 -- MIGRATION: not approved, fix when encountered
      handleLogoutTimer()
    }

    ACTIVITY_EVENTS.forEach((item) => {
      window.addEventListener(item, handleActivity, true)
    })

    // `|| 1` rather than falling through to `length || TIMEOUT_LENGTH`: a remaining time of
    // exactly 0 is falsy, and restarting the full 30-minute window for someone who is
    // already past it is the opposite of the intent.
    handleLogoutTimer(msUntilInactive(safeLocalStorage.get('lastUserAction'), moment().valueOf()) || 1)

    return () => {
      ACTIVITY_EVENTS.forEach((item) => {
        window.removeEventListener(item, handleActivity, true)
      })
      resetTimer()
    }
  }, [])

  return children
}
